// src/components/stories/StoriesTray.tsx
//
// Homepage top section: a horizontal, snap-scrolling row of story bubbles.
// Opens the full-screen StoryViewer on tap. Reserves height to avoid layout
// shift, and renders loading / empty / error states.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { useStories } from '@/hooks/useStories';
import { useStorySeen } from '@/hooks/useStorySeen';
import { StoryBubble } from './StoryBubble';
import { StoryViewer } from './StoryViewer';

export function StoriesTray() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { data: stories, isLoading, isError } = useStories();
  const { isSeen } = useStorySeen();
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  // Reserve height to prevent CLS while loading.
  if (isLoading) {
    return (
      <section aria-busy className="border-b border-border/60 bg-surface-1/40">
        <div className="story-tray overflow-x-auto py-4">
          <div className="mx-auto flex w-max gap-4 px-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex w-[76px] shrink-0 flex-col items-center gap-2">
                <div className="h-[70px] w-[70px] animate-pulse rounded-full bg-muted" />
                <div className="h-2 w-12 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (isError || !stories || stories.length === 0) {
    // Fail quietly — the tray is non-critical chrome above the hero.
    return null;
  }

  return (
    <section
      className="border-b border-border/60 bg-surface-1/40"
      aria-label={isRTL ? 'القصص' : 'Stories'}
    >
      <div className="story-tray overflow-x-auto py-4 scroll-smooth snap-x">
        {/* w-max + mx-auto centers the row when it fits, and left-aligns +
            scrolls (without clipping the start) when it overflows. */}
        <div className="mx-auto flex w-max gap-4 px-4" role="list">
          {stories.map((story, idx) => (
            <div role="listitem" key={story.id}>
              <StoryBubble
                story={story}
                seen={isSeen(story.id)}
                isRTL={isRTL}
                onOpen={() => setOpenIdx(idx)}
              />
            </div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {openIdx !== null && (
          <StoryViewer
            stories={stories}
            startIndex={openIdx}
            isRTL={isRTL}
            onClose={() => setOpenIdx(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
